import React, { useState, useEffect, useRef, useCallback } from 'react';
import ChatHeader from './ChatHeader';
import MessageBubble from './MessageBubble';
import ChatInputBar from './ChatInputBar';
import TypingIndicator from './TypingIndicator';
import AgentAssignModal from './AgentAssignModal';
import { chatApi } from '../../Services/chatService';
import { getChatSocket } from '../../Services/chatService';

const ChatWindow = ({ conversation, agents, role, onConvUpdate, onBack }) => {
    const [messages, setMessages]           = useState([]);
    const [loading, setLoading]             = useState(true);
    const [page, setPage]                   = useState(1);
    const [hasMore, setHasMore]             = useState(true);
    const [otherTyping, setOtherTyping]     = useState(false);
    const [showAssignModal, setShowAssignModal] = useState(false);
    const [isClosed, setIsClosed]           = useState(conversation.status === 'closed');

    const bottomRef  = useRef(null);
    const typingTimer = useRef(null);

    const admin = JSON.parse(localStorage.getItem('admin') || '{}');

    // ── Load message history ─────────────────────────────────────────────────
    const loadMessages = useCallback(async (pg = 1) => {
        try {
            setLoading(pg === 1);
            const { data } = await chatApi.getMessages(conversation._id, { page: pg, limit: 50 });
            if (data.status === 1) {
                const msgs = data.data.messages;
                if (pg === 1) {
                    setMessages(msgs);
                } else {
                    setMessages(prev => [...msgs, ...prev]);
                }
                setHasMore(msgs.length === 50);
            }
        } catch (e) {
            console.error('loadMessages:', e);
        } finally {
            setLoading(false);
        }
    }, [conversation._id]);

    useEffect(() => {
        setMessages([]);
        setPage(1);
        setIsClosed(conversation.status === 'closed');
        loadMessages(1);
    }, [conversation._id, loadMessages, conversation.status]);

    // ── Scroll to bottom on new messages ─────────────────────────────────────
    useEffect(() => {
        bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
    }, [messages]);

    // ── Socket.IO room events ─────────────────────────────────────────────────
    useEffect(() => {
        const socket = getChatSocket();
        if (!socket) return;

        socket.emit('join_conversation', { conversationId: conversation._id });
        socket.emit('mark_read', { conversationId: conversation._id });

        const onNewMessage = (msg) => {
            if (msg.conversationId === conversation._id) {
                setMessages(prev => [...prev, msg]);
                setOtherTyping(false);
                socket.emit('mark_read', { conversationId: conversation._id });
            }
        };
        const onTyping = ({ conversationId, senderType, isTyping }) => {
            if (conversationId === conversation._id && senderType !== 'admin') {
                setOtherTyping(isTyping);
            }
        };
        const onClosed = ({ conversationId }) => {
            if (conversationId === conversation._id) {
                setIsClosed(true);
                onConvUpdate({ _id: conversation._id, status: 'closed' });
            }
        };
        const onAssigned = ({ conversation: conv }) => {
            if (conv._id === conversation._id) {
                onConvUpdate(conv);
            }
        };

        socket.on('new_message',          onNewMessage);
        socket.on('user_typing',          onTyping);
        socket.on('conversation_closed',  onClosed);
        socket.on('chat_assigned',        onAssigned);

        return () => {
            socket.emit('leave_conversation', { conversationId: conversation._id });
            socket.off('new_message',          onNewMessage);
            socket.off('user_typing',          onTyping);
            socket.off('conversation_closed',  onClosed);
            socket.off('chat_assigned',        onAssigned);
        };
    }, [conversation._id, onConvUpdate]);

    // ── Send message (via socket) ─────────────────────────────────────────────
    const handleSend = useCallback((payload) => {
        const socket = getChatSocket();
        if (!socket || !socket.connected) return;
        socket.emit('send_message', {
            conversationId: conversation._id,
            ...payload
        });
    }, [conversation._id]);

    // ── Typing indicator ─────────────────────────────────────────────────────
    const handleTyping = useCallback(() => {
        const socket = getChatSocket();
        if (!socket) return;
        socket.emit('typing', { conversationId: conversation._id, isTyping: true });
        clearTimeout(typingTimer.current);
        typingTimer.current = setTimeout(() => {
            socket.emit('typing', { conversationId: conversation._id, isTyping: false });
        }, 2000);
    }, [conversation._id]);

    // ── Close conversation ────────────────────────────────────────────────────
    const handleClose = async () => {
        if (!window.confirm('Close this conversation?')) return;
        try {
            const { data } = await chatApi.closeConversation(conversation._id);
            if (data.status === 1) {
                setIsClosed(true);
                onConvUpdate({ _id: conversation._id, status: 'closed' });
            }
        } catch (e) { console.error(e); }
    };

    // ── Load more (scroll up) ─────────────────────────────────────────────────
    const handleLoadMore = () => {
        const next = page + 1;
        setPage(next);
        loadMessages(next);
    };

    return (
        <div className="chat-window">
            <ChatHeader
                conversation={conversation}
                role={role}
                onAssign={() => setShowAssignModal(true)}
                onClose={handleClose}
                isClosed={isClosed}
                onBack={onBack}
            />

            <div className="chat-messages">
                {loading && (
                    <div className="chat-loading-msgs">
                        <div className="chat-spinner" />
                        <span>Loading messages...</span>
                    </div>
                )}

                {!loading && hasMore && (
                    <button className="chat-load-more" onClick={handleLoadMore}>
                        Load older messages
                    </button>
                )}

                {messages.map((msg, idx) => (
                    <MessageBubble
                        key={msg._id || idx}
                        message={msg}
                        isOwn={msg.senderType === 'admin'}
                    />
                ))}

                {otherTyping && <TypingIndicator />}
                <div ref={bottomRef} />
            </div>

            <ChatInputBar
                onSend={handleSend}
                onTyping={handleTyping}
                disabled={false}
            />

            {showAssignModal && (
                <AgentAssignModal
                    conversationId={conversation._id}
                    agents={agents}
                    currentAgentId={conversation.assignedAgentId}
                    onAssigned={(conv) => {
                        onConvUpdate(conv);
                        setShowAssignModal(false);
                    }}
                    onClose={() => setShowAssignModal(false)}
                />
            )}
        </div>
    );
};

export default ChatWindow;
